import { z } from 'zod';

/**
 * Acquisition Strategies: a versioned bundle of every acquisition setting, managed in admin
 * like onboarding flow versions. One strategy is active (or two, split by `trafficWeight`, for
 * an A/B test); each new visitor is assigned one for life, and everything downstream — the
 * signup gate, its referral count, later the feature walls — reads the visitor's OWN strategy,
 * never "whatever is active now". Settings freeze once a strategy is activated, so a version's
 * metrics always describe exactly one configuration.
 */

/** First-party anonymous visitor id cookie, set by town-square middleware. */
export const VISITOR_COOKIE = 'ah_vid';
/**
 * An admin's strategy preview: the Preview link carries a visitor id pinned to that strategy in
 * this param; town-square swaps it into `ah_vid` and remembers it here, so the page can say
 * it's a preview and offer the way out.
 */
export const PREVIEW_PARAM = 'acqPreview';
export const PREVIEW_COOKIE = 'ah_acq_preview';

/** Clerk `unsafeMetadata` key that carries the visitor id into provisioning. */
export const VISITOR_METADATA_KEY = 'visitorId';

const PUBLIC_VISITOR_ID = /^[A-Za-z0-9-]{8,64}$/;
const SERVER_VISITOR_PREFIX = /^user-/i;

/**
 * A visitor id a browser may present: what `ah_vid` mints. Ids the server mints for itself
 * (`user-<id>` from before, anything with a `:`) are refused, so no client can name another
 * member's assignment row.
 */
export function isPublicVisitorId(value: string): boolean {
  return PUBLIC_VISITOR_ID.test(value) && !SERVER_VISITOR_PREFIX.test(value);
}

/** GraphQL `extensions.code` for a signed-in user still behind the signup gate. */
export const ACCESS_WAITLISTED = 'ACCESS_WAITLISTED';

/** Town-square path waitlisted users are held on. */
export const WAITLIST_PATH = '/waitlist';

export const SIGNUP_GATE_MODES = ['open', 'refer_to_join', 'invite_required'] as const;
export type SignupGateMode = (typeof SIGNUP_GATE_MODES)[number];

/** What makes a referral count toward a wall: the friend signing up, or their first upload. */
export const QUALIFY_ON = ['signup', 'first_upload'] as const;
export type QualifyOn = (typeof QUALIFY_ON)[number];

export const ACCESS_STATES = ['active', 'waitlisted'] as const;
export type AccessState = (typeof ACCESS_STATES)[number];

export const ACCESS_VIA = ['open', 'invite', 'referral_unlock', 'admin_release', 'time_release'] as const;
export type AccessVia = (typeof ACCESS_VIA)[number];

export const STRATEGY_STATUSES = ['draft', 'active', 'archived'] as const;
export type StrategyStatus = (typeof STRATEGY_STATUSES)[number];

/** At most this many strategies run at once — one, or two for an A/B test. */
export const MAX_ACTIVE_STRATEGIES = 2;

const copyText = z.string().trim().max(280);

export const strategySettingsSchema = z.object({
  signupGate: z
    .object({
      mode: z.enum(SIGNUP_GATE_MODES).default('open'),
      requiredReferrals: z.number().int().min(1).max(50).default(1),
      qualifyOn: z.enum(QUALIFY_ON).default('signup'),
      /** A visitor arriving through a valid referral/invite link skips the gate. */
      referredLinkBypasses: z.boolean().default(true),
      /**
       * The optional release valve: a held signup gets in after this many days even if no friend
       * joined. `null` = off, so a strategy holds people until they refer. Every gated launch that
       * worked had one (Robinhood invited its whole list, Mailbox ran a queue) — see
       * docs/research/acquisition-gates.md — so it exists to be A/B tested, not assumed.
       */
      releaseAfterDays: z.number().int().min(1).max(60).nullable().default(null),
      /**
       * Build while you wait: the onboarding flow version a held member runs on the waitlist
       * (profile steps only; it is private until they're in). `null` = off, and the waitlist page
       * offers only the invite link. Once let in, they finish the new-member wizard's remaining
       * steps — whatever they already answered here is skipped. Dribbble's prospects and The
       * League both let the waitlist build a profile (docs/research/acquisition-gates.md).
       */
      waitingFlowVersionId: z.string().regex(/^\d+$/).nullable().default(null),
    })
    .default({}),
  copy: z
    .object({
      // Waitlist-first, the friend as the fast lane: how Robinhood and Superhuman framed a referral
      // waitlist. "Invite-only" is Clubhouse's model (a member must let you in), not this one.
      waitlistHeadline: copyText.default('Skip the waitlist with a friend'),
      // Reads right for any count: "1 more" and "3 more" both work, where "{count} artist friends" did not.
      waitlistBody: copyText.default("Invite your artist friends. {count} more and you're in."),
      welcomeHeadline: copyText.default("You're in."),
      welcomeBody: copyText.default('{inviter} brought you in, so you skipped the waitlist.'),
      // The waiting wizard (only shown when signupGate.waitingFlowVersionId is set).
      // Under "Another way in, faster / Finish your profile" on the waitlist page.
      waitingPrompt: copyText.default(
        "We let in finished profiles first. Add your photo, a bio and your work. It stays private until you're in."
      ),
      waitingDoneHeadline: copyText.default('Your profile is ready.'),
      waitingDoneBody: copyText.default(
        "You're on the list, and finished profiles are the first we review. We'll let you know the moment you're in."
      ),
    })
    .default({}),
});

export type StrategySettings = z.infer<typeof strategySettingsSchema>;

/** Full settings with every default filled; tolerant of rows written by an older schema. */
export function parseSettings(raw: unknown): StrategySettings {
  const result = strategySettingsSchema.safeParse(raw ?? {});
  if (result.success) {
    return result.data;
  }
  return strategySettingsSchema.parse({});
}

/** Whether a strategy holds cold signups behind a gate. */
export function isGated(settings: StrategySettings): boolean {
  return settings.signupGate.mode !== 'open';
}

/** The onboarding version a held member builds their profile in, or null when the strategy has none. */
export function waitingFlowVersionId(settings: StrategySettings): string | null {
  return isGated(settings) ? settings.signupGate.waitingFlowVersionId : null;
}

/**
 * Onboarding steps that reach other members (following, joining, posting). A waiting flow may
 * not contain them: a held member is invisible until they're in, and runs these afterwards.
 */
export const SOCIAL_STEP_CODES = [
  'FOLLOW_ARTISTS',
  'JOIN_COMMUNITIES',
  'WELCOME_POST',
  'TOP_THREADS',
  'FIRST_CRITIQUE',
  'WARMUP_CRITIQUE',
  'COMPLETION_CRITIQUE',
  'COMPLETION_COMMUNITY',
  'PUSH_PERMISSION',
] as const;

/**
 * Steps a waiting flow can't hold: the social ones, and the finale/completion screens, which
 * celebrate a live profile — a held member gets the waiting "Your profile is ready" screen instead.
 */
export function stepsNotAllowedWhileWaiting(stepCodes: string[]): string[] {
  return stepCodes.filter((code) => {
    return (
      (SOCIAL_STEP_CODES as readonly string[]).includes(code) ||
      code.startsWith('FINALE_') ||
      code.startsWith('COMPLETION_')
    );
  });
}

/** Interpolate `{token}` placeholders in strategy copy. */
export function fillCopy(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (values[key] === undefined) {
      return match;
    }
    return String(values[key]);
  });
}

/** FNV-1a — stable across runtimes, so a visitor lands in the same bucket everywhere. */
export function hashBucket(seed: string, modulo: number): number {
  const hash = Array.from(seed).reduce((memo, char) => {
    return Math.imul(memo ^ char.charCodeAt(0), 16777619) >>> 0;
  }, 2166136261);
  return hash % modulo;
}

interface Weighted {
  id: string;
  trafficWeight: number;
}

/** Deterministic weighted pick; null when nothing has weight. */
export function pickWeighted<T extends Weighted>(seed: string, candidates: T[]): T | null {
  const sorted = [...candidates].filter((c) => c.trafficWeight > 0).sort((a, b) => Number(a.id) - Number(b.id));
  const total = sorted.reduce((memo, c) => memo + c.trafficWeight, 0);
  if (total === 0) {
    return null;
  }
  const bucket = hashBucket(seed, total);
  const found = sorted.reduce<{ pick: T | null; floor: number }>(
    (memo, c) => {
      if (memo.pick) {
        return memo;
      }
      const ceiling = memo.floor + c.trafficWeight;
      return bucket < ceiling ? { pick: c, floor: ceiling } : { pick: null, floor: ceiling };
    },
    { pick: null, floor: 0 }
  );
  return found.pick;
}

/** The weights an activation must satisfy: 1–2 strategies, positive integers summing to 100. */
export function validateWeights(weights: number[]): string | null {
  if (weights.length < 1 || weights.length > MAX_ACTIVE_STRATEGIES) {
    return `Activate 1 to ${MAX_ACTIVE_STRATEGIES} strategies.`;
  }
  if (weights.some((w) => !Number.isInteger(w) || w <= 0)) {
    return 'Every weight must be a positive whole number.';
  }
  if (weights.reduce((memo, w) => memo + w, 0) !== 100) {
    return 'Weights must add up to 100.';
  }
  return null;
}

/** When a signup held at `signedUpAt` gets in regardless, or null when the strategy has no release valve. */
export function releaseDate(signedUpAt: Date, settings: StrategySettings): Date | null {
  const days = settings.signupGate.releaseAfterDays;
  if (days == null) {
    return null;
  }
  return new Date(signedUpAt.getTime() + days * 24 * 60 * 60 * 1000);
}
